import { createSelector } from '@reduxjs/toolkit';
import {
  GALLERY_ACCESS,
  GALLERY_EDIT_OTHER_USERS_IMAGES,
  GALLERY_VIEW_MEDIA_ADDER_INFO,
  PROJECT_BANK_ACCESS,
} from 'scenes/Permissions/constants/permissions';
import {
  selectIsPersonalPermissionsLoaded,
  selectIsPersonalPermissionsLoadedOnce,
  selectIsPersonalPermissionsLoadFailed,
  selectIsRequiredPermission,
} from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import {
  BANK_ACCESS_MODES,
  bankImagesFolderKeyOf,
  bankImagesFolderKeyUnder,
} from 'scenes/ProjectBank/utils/bankAccess';
import { findProjectRootFolder } from 'scenes/ProjectBank/utils/projectRootFolder';
import {
  selectSelectedProject,
  selectSelectedProjectId,
  selectUserProjectsLoaded,
} from 'store/projects/selectors/projectsSelectors';
import { selectLoggedInUser } from 'store/user/selectors.js/user.selectors';

const selectHasBankAccess = (state) =>
  selectIsRequiredPermission(state, PROJECT_BANK_ACCESS);
const selectHasGalleryAccess = (state) =>
  selectIsRequiredPermission(state, GALLERY_ACCESS);

/**
 * How much of the open project's bank the user may use (see
 * utils/bankAccess BANK_ACCESS_MODES): PENDING until the user's permissions
 * are loaded, then FULL, GALLERY_ONLY or NONE. A reload of the permissions
 * (the project view asks for one on every visit) keeps the earlier answer
 * meanwhile: a user who opens the bank from there straight away is not left
 * PENDING, where the bank would treat them as its own user.
 *
 * Bank access is FULL at once: the project entity (and, through it, the
 * owner / admin check and the permissions inherited from a parent project)
 * can only add to what the user holds. Without it the project entity decides
 * — a business admin or owner has both keys — so a user who holds the
 * gallery but not the bank stays PENDING until the entity comes with the
 * user's projects. When it does not come (a business admin or owner opening
 * a project they are not a member of: the user's projects hold only their
 * own), or when the user holds neither key and there is no entity to say
 * otherwise, or when the permissions could not be loaded at all, the bank is
 * FULL, as every bank was before gallery-only users had one: the API applies
 * its own rules to whatever the bank asks for.
 */
export const selectBankAccessMode = createSelector(
  [
    selectIsPersonalPermissionsLoaded,
    selectIsPersonalPermissionsLoadedOnce,
    selectIsPersonalPermissionsLoadFailed,
    selectUserProjectsLoaded,
    selectSelectedProject,
    selectHasBankAccess,
    selectHasGalleryAccess,
  ],
  (
    isPermissionsLoaded,
    isPermissionsLoadedOnce,
    isPermissionsLoadFailed,
    isUserProjectsLoaded,
    project,
    hasBank,
    hasGallery
  ) => {
    if (!isPermissionsLoaded && !isPermissionsLoadedOnce) {
      return BANK_ACCESS_MODES.PENDING;
    }
    if (isPermissionsLoadFailed || hasBank) return BANK_ACCESS_MODES.FULL;
    if (project && project.id) {
      return hasGallery
        ? BANK_ACCESS_MODES.GALLERY_ONLY
        : BANK_ACCESS_MODES.NONE;
    }
    return hasGallery && !isUserProjectsLoaded
      ? BANK_ACCESS_MODES.PENDING
      : BANK_ACCESS_MODES.FULL;
  }
);

/** The user may use only the images scope of the open project's bank. */
export const selectIsBankGalleryOnly = createSelector(
  [selectBankAccessMode],
  (mode) => mode === BANK_ACCESS_MODES.GALLERY_ONLY
);

/**
 * The bank is open as the project's gallery (the Galleria button,
 * `?view=gallery`): its images folder alone, without the sidebar.
 */
export const selectIsProjectBankGalleryView = (state) =>
  Boolean(state.projectBank && state.projectBank.isGalleryView);

// The open project's root folder among the loaded folders, read off the
// slice here rather than through project-bank.selectors, which reads this
// module.
const selectLoadedRootFolder = createSelector(
  [
    (state) =>
      state.projectBank &&
      state.projectBank.projectFolderFilePermissions &&
      state.projectBank.projectFolderFilePermissions.entities,
    selectSelectedProjectId,
  ],
  (entities, projectId) =>
    findProjectRootFolder(Object.values(entities || {}), projectId)
);

/**
 * Key of the open project's images folder (utils/bankAccess): from the
 * project entity, or where the user's projects do not hold it (a business
 * admin in a project they are not a member of), from its loaded root folder.
 */
export const selectBankImagesFolderKey = createSelector(
  [selectSelectedProject, selectLoadedRootFolder],
  (project, rootFolder) =>
    bankImagesFolderKeyOf(project) || bankImagesFolderKeyUnder(rootFolder)
);

// The file picker, which borrows the bank's slice while it is open (over the
// bank as well: an email's attachments, say).
const selectIsFilePickerOpen = (state) =>
  Boolean(
    state.projectBank && state.projectBank.isProjectBankFileSelectDialogOpen
  );

/**
 * What decides which folders may be opened, for utils/bankAccess
 * `canOpenBankFolder(folder, access)`: kept to the images scope for a
 * gallery-only user, and while the bank is open as the project's gallery —
 * a view, not an access, so not in the file picker opened over it — to the
 * images folder itself (`imagesFolderOnly`), where the gallery's new photos
 * go.
 * @returns {{mode: string, imagesKey: ?string, imagesOnly: boolean, imagesFolderOnly: boolean}}
 */
export const selectBankFolderAccess = createSelector(
  [
    selectBankAccessMode,
    selectBankImagesFolderKey,
    selectIsProjectBankGalleryView,
    selectIsFilePickerOpen,
  ],
  (mode, imagesKey, isGalleryView, isFilePickerOpen) => ({
    mode,
    imagesKey,
    imagesOnly:
      mode === BANK_ACCESS_MODES.GALLERY_ONLY ||
      (isGalleryView && !isFilePickerOpen),
    imagesFolderOnly: isGalleryView && !isFilePickerOpen,
  })
);

/**
 * The bank is kept to its images scope (see selectBankFolderAccess), so the
 * loads for the rest of it — the forms, the private bank — are not asked
 * for.
 */
export const selectIsBankKeptToImages = createSelector(
  [selectBankFolderAccess],
  (access) => access.imagesOnly
);

/**
 * A gallery-only user without "view media adder info": no uploader is shown
 * anywhere in the bank (the API leaves others' uploaders out as well).
 */
export const selectBankHidesAuthors = createSelector(
  [
    selectIsBankGalleryOnly,
    (state) => selectIsRequiredPermission(state, GALLERY_VIEW_MEDIA_ADDER_INFO),
  ],
  (isGalleryOnly, maySeeUploaders) => isGalleryOnly && !maySeeUploaders
);

/**
 * The gallery's edit rule, for utils/bankAccess
 * `canEditBankMediaAsGallery(row, rule)` and `getBankItemRights`: their own
 * photos, or anyone's with "edit other users' images". A gallery-only user
 * goes by it alone; for anyone else it adds to the bank's permissions on
 * photos and videos where they have the gallery (`hasGalleryAccess`).
 * @returns {{userId: ?string, canEditOthers: boolean, hasGalleryAccess: boolean}}
 */
export const selectBankGalleryEditRule = createSelector(
  [
    selectLoggedInUser,
    (state) =>
      selectIsRequiredPermission(state, GALLERY_EDIT_OTHER_USERS_IMAGES),
    selectHasGalleryAccess,
  ],
  (user, canEditOthers, hasGalleryAccess) => ({
    userId: (user && user.id) || null,
    canEditOthers: Boolean(canEditOthers),
    hasGalleryAccess: Boolean(hasGalleryAccess),
  })
);
